import React, { FunctionComponent } from 'react';
import { Form, Field } from 'react-final-form';
import { Typography, CircularProgress } from '@mui/material';
import i18next from 'i18next';

import Input from 'components/inputs/Input';
import {
  composeValidators,
  requiredValidation,
  passwordValidation,
  passwordConfirmationValidation,
} from 'helpers/validations';

import {
  ForgotPasswordContainer,
  StyledForgotPaper,
  FormWrapper,
  InputSpacing,
  MessageBanner,
  StyledSubmitButton,
} from './styles';
import './i18n';

interface Props {
  onSubmit: (password: string) => void;
  loading: boolean;
  message: string | null;
  hasParams: boolean;
}

const ChangePasswordView: FunctionComponent<Props> = ({
  onSubmit,
  loading,
  message,
  hasParams,
}) => {
  if (!hasParams) {
    return (
      <ForgotPasswordContainer>
        <StyledForgotPaper>
          <Typography color="error" align="center" variant="h6">
            {i18next.t<string>('CHANGE_PASSWORD:INVALID_LINK')}
          </Typography>
        </StyledForgotPaper>
      </ForgotPasswordContainer>
    );
  }

  const isSuccess = !!message?.toLowerCase().includes('éxito');

  return (
    <ForgotPasswordContainer>
      <StyledForgotPaper elevation={0}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {i18next.t<string>('CHANGE_PASSWORD:FORM_TITLE')}
        </Typography>
        <Typography variant="body1" color="text.secondary" fontWeight={300}>
          {i18next.t<string>('CHANGE_PASSWORD:FORM_SUBTITLE')}
        </Typography>

        <FormWrapper>
          <Form
            onSubmit={(values) => onSubmit(values.password)}
            render={({ handleSubmit, pristine, invalid }) => (
              <form onSubmit={handleSubmit}>
                <InputSpacing>
                  <Field
                    name="password"
                    // Validamos: requerido + complejidad de caracteres
                    validate={composeValidators(requiredValidation, passwordValidation)}
                  >
                    {({ input, meta }) => (
                      <Input
                        input={{ ...input, type: 'password' }}
                        meta={meta}
                        label={i18next.t<string>('CHANGE_PASSWORD:NEW_PWD_LABEL')}
                      />
                    )}
                  </Field>
                </InputSpacing>

                <InputSpacing>
                  <Field
                    name="confirmPassword"
                    validate={composeValidators(requiredValidation, passwordConfirmationValidation)}
                  >
                    {({ input, meta }) => (
                      <Input
                        input={{ ...input, type: 'password' }}
                        meta={meta}
                        label={i18next.t<string>('CHANGE_PASSWORD:CONFIRM_PWD_LABEL')}
                      />
                    )}
                  </Field>
                </InputSpacing>

                {message && <MessageBanner $isSuccess={isSuccess}>{message}</MessageBanner>}

                <StyledSubmitButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || pristine || invalid}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    i18next.t<string>('CHANGE_PASSWORD:SUBMIT')
                  )}
                </StyledSubmitButton>
              </form>
            )}
          />
        </FormWrapper>
      </StyledForgotPaper>
    </ForgotPasswordContainer>
  );
};

export default ChangePasswordView;
